import {
    Box,
    Button,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { StatesResponse } from "../../../../api/locations";
import { fetchCep, fetchStates } from "../../../../api/locations";
import CepTextField from "../../../../components/CepTextField";
import { removeNotNumbers } from "../../../../utils/removeNotNumbers";

export interface AddressStepProps {
    handleNext: () => void;
    handleBack: () => void;
    handleCancel: () => void;
}

function AddressStep({
                         handleNext,
                         handleBack,
                         handleCancel,
                     }: AddressStepProps): React.JSX.Element {
    const { t } = useTranslation();
    const [ufs, setUfs] = useState<StatesResponse[]>([]);
    const [isFocusCep, setIsFocusCep] = useState<boolean>(false);
    const [isAddressFieldsEditable, setIsAddressFieldsEditable] =
        useState<boolean>(false);

    const lastProcessedCepRef = useRef<string | null>(null);

    const { control, setValue, setError, clearErrors, watch } = useFormContext();

    const cepValue = (watch("cep") as string) ?? "";
    const stateValue = (watch("state") as string) ?? "";
    const cityValue = (watch("city") as string) ?? "";
    const streetValue = (watch("street") as string) ?? "";
    const neighborhoodValue = (watch("neighborhood") as string) ?? "";
    const streetNumberValue = (watch("streetNumber") as string) ?? "";

    const canProceed = useMemo(() => {
        const normalizedCep = removeNotNumbers(cepValue);

        const hasCepWithEightDigits = normalizedCep.length === 8;

        const hasRequiredAddressFields =
            Boolean(stateValue) &&
            Boolean(cityValue) &&
            Boolean(streetValue) &&
            Boolean(neighborhoodValue) &&
            Boolean(streetNumberValue);

        return !isFocusCep && hasCepWithEightDigits && hasRequiredAddressFields;
    }, [
        cepValue,
        stateValue,
        cityValue,
        streetValue,
        neighborhoodValue,
        streetNumberValue,
        isFocusCep,
    ]);

    const clearFields = (): void => {
        setValue("street", "");
        setValue("neighborhood", "");
        setValue("complement", "");
        setValue("city", "");
        setValue("state", "");
    };

    const onBlurApiField = async (
        e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    ): Promise<void> => {
        const rawCep = e.target.value;
        const normalizedCep = removeNotNumbers(rawCep);

        setIsFocusCep(false);

        if (
            lastProcessedCepRef.current !== null &&
            normalizedCep === lastProcessedCepRef.current
        ) {
            setIsAddressFieldsEditable(normalizedCep.length === 8);
            return;
        }

        lastProcessedCepRef.current = normalizedCep;

        if (!normalizedCep) {
            setIsAddressFieldsEditable(false);
            clearFields();
            setError("cep", {
                message: t("register.cepRequired"),
            });
            return;
        }

        if (normalizedCep.length !== 8) {
            setIsAddressFieldsEditable(false);
            clearFields();
            setError("cep", {
                message: t("register.cepDigits"),
            });
            return;
        }

        setIsAddressFieldsEditable(true);
        clearFields();

        try {
            const data = await fetchCep(normalizedCep);

            if (data.erro) {
                setError("cep", {
                    message: t("register.cepInvalid"),
                });
                return;
            }

            clearErrors("cep");

            setValue("state", data.uf);
            setValue("city", data.localidade);
            setValue("street", data.bairro);
            setValue("neighborhood", data.logradouro);
            setValue("complement", data.complemento);
        } catch {
            setError("cep", {
                message: t("register.cepInvalid"),
            });
        }
    };

    const getStates = async (): Promise<void> => {
        const data = await fetchStates();
        setUfs(data);
    };

    useEffect(() => {
        void getStates();
    }, []);

    const handleNextClick = (): void => {
        clearErrors("cep");
        handleNext();
    };

    return (
        <Stack alignItems="center" spacing={1}>
            <Controller
                control={control}
                name="cep"
                render={({ field, fieldState: { error } }) => (
                    <CepTextField
                        {...field}
                        error={Boolean(error)}
                        fullWidth
                        helperText={
                            isFocusCep && !field.value ? (
                                <Typography
                                    color="common.black"
                                    component="div"
                                    variant="caption"
                                >
                                    {t("common.numbersOnly")}
                                </Typography>
                            ) : (
                                error?.message || " "
                            )
                        }
                        onBlur={(event) => void onBlurApiField(event)}
                        onFocus={() => {
                            setIsFocusCep(true);
                        }}
                        variant="outlined"
                    />
                )}
            />

            <Grid container size={12} style={{ justifyContent: "space-between" }}>
                <Controller
                    control={control}
                    name="state"
                    render={({ field, fieldState: { error } }) => (
                        <FormControl
                            disabled={!isAddressFieldsEditable}
                            sx={{
                                width: "20%",
                                "& .MuiInputLabel-root": {
                                    color: isAddressFieldsEditable
                                        ? "primary.main"
                                        : "text.disabled",
                                },
                                "& .MuiInputLabel-root.Mui-focused": {
                                    color: "primary.main",
                                },
                                "& .MuiInputLabel-root.Mui-disabled": {
                                    color: "text.disabled",
                                },
                                "& .MuiInputBase-input": {
                                    color: "primary.main",
                                },
                            }}
                        >
                            <InputLabel color="primary" id="demo-simple-select-label">
                                {t("register.ufLabel")}
                            </InputLabel>
                            <Select
                                {...field}
                                color="primary"
                                error={Boolean(error)}
                                id="demo-simple-select"
                                label={`${t("register.ufLabel")} *`}
                                labelId="demo-simple-select-label"
                                variant="outlined"
                            >
                                {ufs.map((item) => {
                                    return (
                                        <MenuItem key={item.id} value={item.sigla}>
                                            {item.sigla}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    )}
                />

                <Controller
                    control={control}
                    name="city"
                    render={({ field, fieldState: { error } }) => (
                        <TextField
                            {...field}
                            color="primary"
                            disabled={!isAddressFieldsEditable}
                            error={Boolean(error)}
                            helperText={error?.message || " "}
                            label={t("register.cityLabel")}
                            style={{ width: "78%" }}
                            sx={{
                                "& .MuiInputLabel-root": {
                                    color: isAddressFieldsEditable
                                        ? "primary.main"
                                        : "text.disabled",
                                },
                                "& .MuiInputLabel-root.Mui-focused": {
                                    color: "primary.main",
                                },
                                "& .MuiInputLabel-root.Mui-disabled": {
                                    color: "text.disabled",
                                },
                                "& .MuiInputBase-input": {
                                    color: "primary.main",
                                },
                            }}
                            variant="outlined"
                        />
                    )}
                />
            </Grid>

            <Controller
                control={control}
                name="street"
                render={({ field, fieldState: { error } }) => (
                    <TextField
                        {...field}
                        color="primary"
                        disabled={!isAddressFieldsEditable}
                        error={Boolean(error)}
                        fullWidth
                        helperText={error?.message || " "}
                        label={t("register.neighborhoodLabel")}
                        sx={{
                            "& .MuiFormLabel-root": {
                                color: "primary.main",
                            },
                            "& .MuiInputBase-input": {
                                color: "primary.main",
                            },
                        }}
                        variant="outlined"
                    />
                )}
            />

            <Controller
                control={control}
                name="neighborhood"
                render={({ field, fieldState: { error } }) => (
                    <TextField
                        {...field}
                        color="primary"
                        disabled={!isAddressFieldsEditable}
                        error={Boolean(error)}
                        fullWidth
                        helperText={error?.message || " "}
                        label={t("register.streetLabel")}
                        sx={{
                            "& .MuiFormLabel-root": {
                                color: "primary.main",
                            },
                            "& .MuiInputBase-input": {
                                color: "primary.main",
                            },
                        }}
                        variant="outlined"
                    />
                )}
            />

            <Controller
                control={control}
                name="streetNumber"
                render={({ field, fieldState: { error } }) => (
                    <TextField
                        {...field}
                        color="primary"
                        disabled={!isAddressFieldsEditable}
                        error={Boolean(error)}
                        fullWidth
                        helperText={error?.message || " "}
                        label={t("register.streetNumberLabel")}
                        sx={{
                            "& .MuiFormLabel-root": {
                                color: "primary.main",
                            },
                            "& .MuiInputBase-input": {
                                color: "primary.main",
                            },
                        }}
                        variant="outlined"
                    />
                )}
            />

            <Controller
                control={control}
                name="complement"
                render={({ field, fieldState: { error } }) => (
                    <TextField
                        {...field}
                        color="primary"
                        disabled={!isAddressFieldsEditable}
                        error={Boolean(error)}
                        fullWidth
                        helperText={error?.message || " "}
                        label={t("register.complementLabel")}
                        sx={{
                            "& .MuiFormLabel-root": {
                                color: "primary.main",
                            },
                            "& .MuiInputBase-input": {
                                color: "primary.main",
                            },
                        }}
                        variant="outlined"
                    />
                )}
            />

            <Box
                alignItems="center"
                display="flex"
                justifyContent="space-between"
                marginTop="5%"
                width="100%"
            >
                <Button
                    color="primary"
                    onClick={handleBack}
                    size="large"
                    tabIndex={-1}
                    type="button"
                    variant="outlined"
                >
                    {t("common.previous")}
                </Button>
                <Button
                    color="primary"
                    disabled={!canProceed}
                    onClick={handleNextClick}
                    size="large"
                    tabIndex={0}
                    type="button"
                    variant="contained"
                >
                    {t("common.next")}
                </Button>
            </Box>

            <Box marginTop="10%" textAlign="end" width="100%">
                <Button
                    color="success"
                    component={Link}
                    onClick={handleCancel}
                    size="medium"
                    sx={{ marginTop: "24px" }}
                    to=""
                    variant="text"
                >
                    {t("common.cancelRegistration")}
                </Button>
            </Box>
        </Stack>
    );
}

export default AddressStep;
